import { useState } from "react"
import { toast } from "react-toastify"
import {
  CommentPlatform,
  CommentPullResult,
  useLazyPullCommentsQuery,
} from "~/ui-rtk/api/commentModerationApi"
import Dialog from "~/ui-rtk/components/ui/common/Dialog"
import Button from "~/ui-rtk/components/ui/controls/Button"
import Input from "~/ui-rtk/components/ui/controls/Input"
import Select from "~/ui-rtk/components/ui/controls/Select"
import { INPUT_CLASSES, INPUT_CLS, SELECT_CLS } from "./ModerationSettings"

const DIALOG_CLASSES = {
  container:
    "bg-basic-black border border-system-blue-600/40 p-6 rounded-xl text-left text-white",
}

function formatTime(iso?: string): string {
  if (!iso) return ""
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleString([], {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
}

/**
 * "Can we pull this brand's comments at all?" — a read-only probe that
 * fetches the raw comments of the first spending post(s) of one platform
 * straight from Meta and shows them. Nothing is judged, hidden or stored, so
 * it works before the ledger exists and before any settings are saved.
 */
export default function CommentPullProbe() {
  const [platform, setPlatform] = useState<CommentPlatform>("INSTAGRAM")
  const [maxPosts, setMaxPosts] = useState(1)
  // 0 = no window: every comment Meta returns on the post.
  const [lookbackDays, setLookbackDays] = useState(0)
  const [pull, { isFetching }] = useLazyPullCommentsQuery()
  const [result, setResult] = useState<CommentPullResult | null>(null)
  const [open, setOpen] = useState(false)

  const handlePull = async () => {
    try {
      const data = await pull(
        { platform, maxPosts, commentLookbackDays: lookbackDays },
        false,
      ).unwrap()
      setResult(data)
      setOpen(true)
    } catch (err) {
      const message = (err as { data?: { message?: string } })?.data?.message
      toast.error(message || "Could not pull comments from Meta.")
    }
  }

  const totalComments =
    result?.posts.reduce((n, p) => n + p.comments.length, 0) ?? 0

  return (
    <section className="flex flex-col gap-system-2xs">
      <span className="text-sm font-medium text-system-gray-200">
        Test: pull comments from Meta
      </span>
      <p className="text-system-gray-300 text-xs">
        Reads the comments of the highest-spend post(s) of one platform and
        shows them raw. Nothing is judged, hidden or saved — this only answers
        whether the credential can read them. Only comments inside the chosen
        window are read; widen it to see an older post&apos;s full thread.
      </p>
      <div className="flex flex-wrap items-center gap-system-sm">
        <Select
          className={SELECT_CLS}
          value={platform}
          onChange={e => setPlatform(e.target.value as CommentPlatform)}
          options={[
            { label: "Instagram", value: "INSTAGRAM" },
            { label: "Facebook", value: "FACEBOOK" },
          ]}
        />
        <label className="flex items-center gap-1 text-xs text-system-gray-300">
          posts
          <Input
            type="number"
            min={1}
            max={10}
            width={64}
            value={maxPosts}
            onChange={e =>
              setMaxPosts(
                Math.min(10, Math.max(1, Number(e.target.value) || 1)),
              )
            }
            className={INPUT_CLS}
            classes={INPUT_CLASSES}
          />
        </label>
        <label className="flex items-center gap-1 text-xs text-system-gray-300">
          comments from the last
          <Select
            className={SELECT_CLS}
            value={lookbackDays}
            onChange={e => setLookbackDays(Number(e.target.value))}
            options={[
              { label: "all time", value: 0 },
              { label: "14 days", value: 14 },
              { label: "30 days", value: 30 },
              { label: "90 days", value: 90 },
              { label: "365 days", value: 365 },
            ]}
          />
        </label>
        <Button
          size="xs"
          variant={{ variant: "outlined" }}
          onClick={() => void handlePull()}
          disabled={isFetching}
        >
          {isFetching ? "Pulling…" : "Pull comments"}
        </Button>
      </div>

      {result && (
        <Dialog
          isOpen={open}
          handleClose={() => setOpen(false)}
          width={720}
          classes={DIALOG_CLASSES}
          closeOnClickOutside
        >
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="text-base font-semibold">
                {totalComments} comment{totalComments === 1 ? "" : "s"} pulled
              </h3>
              <p className="text-xs text-system-gray-400">
                {result.adsScanned} ads scanned · {result.postsFound} distinct
                posts · showing {result.posts.length} ({platform.toLowerCase()})
                · comments{" "}
                {new Date(result.since).getTime() === 0
                  ? "from all time"
                  : `since ${formatTime(result.since)}`}
              </p>
            </div>
            <Button
              size="xs"
              variant={{ variant: "text" }}
              onClick={() => setOpen(false)}
            >
              Close
            </Button>
          </div>

          <div className="mt-4 flex max-h-[60vh] flex-col gap-4 overflow-y-auto pr-1">
            {result.posts.length === 0 && (
              <p className="text-sm text-system-gray-400">
                No spending {platform.toLowerCase()} post in the last 7 days.
              </p>
            )}
            {result.posts.map(post => (
              <section
                key={`${post.platform}:${post.postId}`}
                className="rounded-lg border border-white/10 bg-white/5 p-3"
              >
                <div className="flex flex-wrap items-baseline justify-between gap-2 text-xs">
                  <div className="min-w-0">
                    <div className="truncate text-sm text-system-gray-100">
                      {post.source === "ORGANIC"
                        ? `Organic post ${post.postId}`
                        : (post.adNames[0] ?? post.postId)}
                      {post.adNames.length > 1
                        ? ` +${post.adNames.length - 1} more`
                        : ""}
                    </div>
                    <div className="text-system-gray-500">
                      {post.platform === "FACEBOOK" ? "Facebook" : "Instagram"}{" "}
                      · post{" "}
                      {post.permalinkUrl ? (
                        <a
                          href={post.permalinkUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="underline hover:text-white"
                        >
                          {post.postId}
                        </a>
                      ) : (
                        <span className="font-mono">{post.postId}</span>
                      )}{" "}
                      · ${post.spend} spend ·{" "}
                      {post.viaPageToken
                        ? "read with the Page token"
                        : "read with the brand credential (no Page token)"}
                    </div>
                  </div>
                  <span className="font-mono text-system-gray-300">
                    {post.comments.length} comment
                    {post.comments.length === 1 ? "" : "s"} in window
                  </span>
                </div>
                {post.error && (
                  <p className="mt-2 rounded-md bg-rose-500/10 px-3 py-2 text-xs text-rose-300">
                    {post.error}
                  </p>
                )}
                {post.comments.length > 0 && (
                  <ul className="mt-3 flex flex-col gap-2">
                    {post.comments.map(c => (
                      <li
                        key={c.commentId}
                        className={`text-xs ${c.parentCommentId ? "ml-4 border-l border-white/10 pl-3" : ""}`}
                      >
                        <div className="text-system-gray-500">
                          {c.authorName ?? "someone"}
                          {c.createdAt ? ` · ${formatTime(c.createdAt)}` : ""}
                          {c.isHidden ? " · already hidden" : ""}
                        </div>
                        <div className="whitespace-pre-wrap break-words text-system-gray-100">
                          {c.message || (
                            <em className="text-system-gray-500">(no text)</em>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            ))}
          </div>
        </Dialog>
      )}
    </section>
  )
}
